import { useCallback, useEffect, useRef, useState } from 'react';
import Raven from 'raven-js';
import { useEffectEvent } from 'academy-common-lib/hooks/useEffectEvent';
import { fetchConversations } from '../resources/conversations/resolver';
import { useConversationsListResource } from '../resources/conversations/hooks';
const PAGE_LIMIT = 20;
export const useConversationsList = agentId => {
  const {
    data,
    loading,
    error,
    invalidate
  } = useConversationsListResource({
    agentId
  });
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const seededDataRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const nextOffsetRef = useRef(undefined);
  const [initialLoading, setInitialLoading] = useState(true);
  const invalidateOnMount = useEffectEvent(() => invalidate());
  useEffect(() => {
    invalidateOnMount();
  }, []);
  useEffect(() => {
    if (isLoadingMoreRef.current) return;
    if (data && data !== seededDataRef.current) {
      var _data$conversations;
      seededDataRef.current = data;
      const incoming = (_data$conversations = data.conversations) !== null && _data$conversations !== void 0 ? _data$conversations : [];
      setItems(incoming);
      setHasMore(Boolean(data.hasMore));
      nextOffsetRef.current = data.offset;
      setInitialLoading(false);
    }
  }, [data]);
  useEffect(() => {
    if (error && !loading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInitialLoading(false);
    }
  }, [error, loading]);
  const refetch = useCallback(() => {
    invalidate();
  }, [invalidate]);
  const loadMore = useCallback(() => {
    if (!hasMore || loading || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    fetchConversations({
      agentId,
      limit: PAGE_LIMIT,
      offset: nextOffsetRef.current,
      scope: 'OWN'
    }).then(payload => {
      var _payload$conversation;
      const next = (_payload$conversation = payload === null || payload === void 0 ? void 0 : payload.conversations) !== null && _payload$conversation !== void 0 ? _payload$conversation : [];
      const moreFlag = Boolean(payload === null || payload === void 0 ? void 0 : payload.hasMore);
      nextOffsetRef.current = payload === null || payload === void 0 ? void 0 : payload.offset;
      if (next.length === 0) {
        setHasMore(moreFlag);
        return;
      }
      setItems(prev => {
        const seen = new Set(prev.map(r => r.conversationId));
        const merged = [...prev];
        for (const row of next) {
          if (!seen.has(row.conversationId)) {
            merged.push(row);
            seen.add(row.conversationId);
          }
        }
        return merged;
      });
      setHasMore(moreFlag);
    }).catch(Raven.captureException.bind(Raven)).finally(() => {
      isLoadingMoreRef.current = false;
    });
  }, [agentId, hasMore, loading]);
  const prepend = useCallback(row => {
    setItems(prev => {
      if (prev.some(r => r.conversationId === row.conversationId)) {
        return prev;
      }
      return [row, ...prev];
    });
  }, []);
  const patch = useCallback((id, fields) => {
    setItems(prev => prev.map(row => row.conversationId === id ? Object.assign({}, row, fields) : row));
  }, []);
  const remove = useCallback(id => {
    setItems(prev => prev.filter(row => row.conversationId !== id));
  }, []);
  const restore = useCallback((row, index) => {
    setItems(prev => {
      if (prev.some(r => r.conversationId === row.conversationId)) {
        return prev;
      }
      const clampedIndex = Math.max(0, Math.min(prev.length, index));
      const next = [...prev];
      next.splice(clampedIndex, 0, row);
      return next;
    });
  }, []);
  return {
    items,
    hasMore,
    loading,
    initialLoading,
    error,
    refetch,
    loadMore,
    prepend,
    patch,
    remove,
    restore
  };
};